import { formatTime } from '@components/AudioPlayer/components/ProgressBar/helpers/formatTime';
import { GetJobUtterancesResponseDto } from 'app/API';
import { intervalToDuration } from 'date-fns';
import { Fragment, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { alignTranscriptWords, TAlignedWordPair } from '../../helpers/alignTranscriptWords';
import { flattenCompareTokens } from '../../helpers/flattenCompareTokens';
import { formatSpeakerLabel } from '../../helpers/formatSpeakerLabel';
import { getSpeakerIndexLabel } from '../../helpers/getSpeakerIndexLabel';
import { scrollSearchMatchIntoView } from '../../helpers/scrollSearchMatchIntoView';
import { useTranscriptSearch } from '../../hooks/useTranscriptSearch';
import type { TSearchHighlight } from '../../types/TSearch';
import { TranscriptSearchBox } from '../TranscriptSearchBox';
import { TranscriptSearchHighlight } from '../TranscriptSearchHighlight';

type TComparableTranscriptWord = {
  utteranceIndex: number;
  text: string;
  normalizedText: string;
  uniqueCharacters: string[];
  start: number;
};

type TTimestampField = 'primaryStart' | 'compareStart';

export type TranscriptCompareOperation = 'match' | 'addition' | 'deletion' | 'substitution';

export type TranscriptCompareToken = {
  operation: TranscriptCompareOperation;
  primaryText?: string;
  compareText?: string;
  primaryStart?: number;
  compareStart?: number;
  timeDeltaMs?: number;
};

export type TranscriptCompareUtterance = {
  speaker: string;
  start: number;
  end?: number;
  tokens: TranscriptCompareToken[];
};

type TProps = {
  utterances: TranscriptCompareUtterance[];
  playbackTime: number;
  onWordClick: (timestamp: number) => void;
  maxColorTimeDeltaMs: number;
  searchFocusSignal?: number;
};

const NEGATIVE_COLOR = { red: 236, green: 72, blue: 153 };
const POSITIVE_COLOR = { red: 30, green: 64, blue: 175 };
const MAX_COLOR_ALPHA = 0.8;

const convertSecondsToTime = (seconds: number) => {
  const { hours, minutes, seconds: sec } = intervalToDuration({ start: 0, end: seconds });
  return `${String(hours || 0).padStart(2, '0')}:${String(minutes || 0).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const exactOrPunctuationOrCapitalisationEdit = (original: string, enhanced: string) => {
  const originalTrimmed = original.trim();
  const enhancedTrimmed = enhanced.trim();

  if (originalTrimmed === enhancedTrimmed) {
    return true;
  }

  const punctuationRegex = /[^\w\s-]/g;
  const originalPunctuationless = originalTrimmed
    .toLowerCase()
    .replace(/--/g, '-')
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(punctuationRegex, '');
  const enhancedPunctuationless = enhancedTrimmed
    .toLowerCase()
    .replace(/--/g, '-')
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(punctuationRegex, '');

  return originalPunctuationless === enhancedPunctuationless;
};

const getTimestampForField = (token: TranscriptCompareToken, field: TTimestampField) => token[field];

const getRgba = (color: typeof POSITIVE_COLOR, alpha: number) =>
  `rgba(${color.red}, ${color.green}, ${color.blue}, ${alpha})`;

const flattenTranscriptWords = (utterances: GetJobUtterancesResponseDto['utterances']): TComparableTranscriptWord[] =>
  utterances.flatMap((utterance, utteranceIndex) =>
    utterance.words
      .filter((word) => word.text.trim().length > 0)
      .map((word) => {
        const normalizedText = normalizeTranscriptCompareWord(word.text);

        return {
          utteranceIndex,
          text: word.text,
          normalizedText,
          uniqueCharacters: getUniqueTranscriptCompareCharacters(normalizedText),
          start: word.start,
        };
      }),
  );

const scoreComparableTranscriptPair = (
  primaryWord: TComparableTranscriptWord,
  compareWord: TComparableTranscriptWord,
) => {
  if (primaryWord.normalizedText === compareWord.normalizedText) {
    return 1;
  }

  if (getUniqueCharacterDifference(primaryWord.uniqueCharacters, compareWord.uniqueCharacters) <= 1) {
    return 0;
  }

  return -2;
};

const areComparableWordsExactMatch = (primaryWord: TComparableTranscriptWord, compareWord: TComparableTranscriptWord) =>
  exactOrPunctuationOrCapitalisationEdit(primaryWord.text, compareWord.text);

const buildTranscriptCompareToken = (
  pair: TAlignedWordPair<TComparableTranscriptWord, TComparableTranscriptWord>,
): TranscriptCompareToken => {
  if (pair.primary && pair.compare) {
    return {
      operation: areComparableWordsExactMatch(pair.primary, pair.compare) ? 'match' : 'substitution',
      primaryText: pair.primary.text,
      compareText: pair.compare.text,
      primaryStart: pair.primary.start,
      compareStart: pair.compare.start,
      timeDeltaMs: pair.compare.start - pair.primary.start,
    };
  }

  if (pair.primary) {
    return {
      operation: 'deletion',
      primaryText: pair.primary.text,
      primaryStart: pair.primary.start,
    };
  }

  const compareWord = pair.compare!;
  return {
    operation: 'addition',
    compareText: compareWord.text,
    compareStart: compareWord.start,
  };
};

const normalizeTranscriptCompareWord = (word: string) =>
  word
    .trim()
    .toLowerCase()
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/[^\w\s-]/g, '')
    .trim();

const getUniqueTranscriptCompareCharacters = (word: string) =>
  Array.from(new Set(word.replace(/\s+/g, '').split(''))).sort();

const getUniqueCharacterDifference = (leftCharacters: string[], rightCharacters: string[]) => {
  let leftIndex = 0;
  let rightIndex = 0;
  let difference = 0;

  while (leftIndex < leftCharacters.length && rightIndex < rightCharacters.length) {
    const leftCharacter = leftCharacters[leftIndex];
    const rightCharacter = rightCharacters[rightIndex];

    if (leftCharacter === rightCharacter) {
      leftIndex++;
      rightIndex++;
      continue;
    }

    difference++;

    if (leftCharacter < rightCharacter) {
      leftIndex++;
    } else {
      rightIndex++;
    }
  }

  return difference + (leftCharacters.length - leftIndex) + (rightCharacters.length - rightIndex);
};

export const buildTranscriptComparison = (
  primaryUtterances: GetJobUtterancesResponseDto['utterances'],
  compareUtterances: GetJobUtterancesResponseDto['utterances'],
): TranscriptCompareUtterance[] => {
  const utterances = primaryUtterances.map((utterance) => ({
    speaker: utterance.speaker,
    start: utterance.start,
    end: utterance.end,
    tokens: [] as TranscriptCompareToken[],
  }));

  const primaryWords = flattenTranscriptWords(primaryUtterances);
  const compareWords = flattenTranscriptWords(compareUtterances);
  const alignment = alignTranscriptWords(primaryWords, compareWords, 1, -1, -1, scoreComparableTranscriptPair);

  const previousPrimaryUtteranceIndices: Array<number | null> = [];
  let previousPrimaryUtteranceIndex: number | null = null;
  for (let index = 0; index < alignment.length; index++) {
    const primaryWord = alignment[index].primary;
    if (primaryWord) {
      previousPrimaryUtteranceIndex = primaryWord.utteranceIndex;
    }
    previousPrimaryUtteranceIndices[index] = previousPrimaryUtteranceIndex;
  }

  const nextPrimaryUtteranceIndices = new Array<number | null>(alignment.length).fill(null);
  let nextPrimaryUtteranceIndex: number | null = null;
  for (let index = alignment.length - 1; index >= 0; index--) {
    const primaryWord = alignment[index].primary;
    if (primaryWord) {
      nextPrimaryUtteranceIndex = primaryWord.utteranceIndex;
    }
    nextPrimaryUtteranceIndices[index] = nextPrimaryUtteranceIndex;
  }

  alignment.forEach((pair, index) => {
    const targetUtteranceIndex =
      pair.primary?.utteranceIndex ?? nextPrimaryUtteranceIndices[index] ?? previousPrimaryUtteranceIndices[index];

    if (targetUtteranceIndex === null || targetUtteranceIndex === undefined) {
      return;
    }

    utterances[targetUtteranceIndex].tokens.push(buildTranscriptCompareToken(pair));
  });

  return utterances;
};

const getNextTimestampForField = (
  tokens: TranscriptCompareUtterance['tokens'],
  currentIndex: number,
  field: TTimestampField,
  fallbackEnd?: number,
) => {
  for (let index = currentIndex + 1; index < tokens.length; index++) {
    const timestamp = getTimestampForField(tokens[index], field);
    if (typeof timestamp === 'number') {
      return timestamp;
    }
  }

  return fallbackEnd;
};

const getTokenEnd = (
  tokens: TranscriptCompareUtterance['tokens'],
  currentIndex: number,
  start?: number,
  field?: TTimestampField,
  fallbackEnd?: number,
) => {
  const nextTimestamp =
    field && typeof start === 'number'
      ? getNextTimestampForField(tokens, currentIndex, field, fallbackEnd)
      : fallbackEnd;

  return typeof start === 'number' && typeof nextTimestamp === 'number' && nextTimestamp > start
    ? nextTimestamp
    : (start ?? 0) + 1;
};

const getBackgroundColor = (timeDeltaMs: number | undefined, maxColorTimeDeltaMs: number) => {
  if (typeof timeDeltaMs !== 'number' || timeDeltaMs === 0) {
    return undefined;
  }

  if (maxColorTimeDeltaMs <= 0) {
    return getRgba(timeDeltaMs > 0 ? POSITIVE_COLOR : NEGATIVE_COLOR, MAX_COLOR_ALPHA);
  }

  const alpha = (Math.min(Math.abs(timeDeltaMs), maxColorTimeDeltaMs) / maxColorTimeDeltaMs) * MAX_COLOR_ALPHA;
  return getRgba(timeDeltaMs > 0 ? POSITIVE_COLOR : NEGATIVE_COLOR, alpha);
};

type CompareUtteranceRowProps = {
  utterance: TranscriptCompareUtterance;
  utteranceIdx: number;
  speakerNameLength: number;
  currentTimeMs: number;
  maxColorTimeDeltaMs: number;
  highlightMap: Map<string, TSearchHighlight[]>;
  currentMatchIndex: number;
  onWordClick: (timestamp: number) => void;
};

const CompareUtteranceRow = memo(
  ({
    utterance,
    utteranceIdx,
    speakerNameLength,
    currentTimeMs,
    maxColorTimeDeltaMs,
    highlightMap,
    currentMatchIndex,
    onWordClick,
  }: CompareUtteranceRowProps) => (
    <div className="flex min-w-0 flex-col md:flex-row">
      <span
        className="flex-shrink-0 min-w-0 max-w-full inline-block mr-4 font-mono whitespace-normal md:whitespace-nowrap"
        style={{ width: `${speakerNameLength + 12}ch` }}
      >
        <span>{convertSecondsToTime(utterance.start)}</span> | <span>{formatSpeakerLabel(utterance.speaker)}:</span>
      </span>

      <span className="inline-block">
        {utterance.tokens.map((token, tokenIdx) => {
          const backgroundColor = getBackgroundColor(token.timeDeltaMs, maxColorTimeDeltaMs);
          const renderWord = ({
            text,
            start,
            field,
            className,
            isStrong,
            searchKey,
          }: {
            text?: string;
            start?: number;
            field?: TTimestampField;
            className?: string;
            isStrong?: boolean;
            searchKey?: string;
          }) => {
            const tokenEnd = getTokenEnd(utterance.tokens, tokenIdx, start, field, utterance.end);
            const isActive = typeof start === 'number' && currentTimeMs >= start && currentTimeMs < tokenEnd;
            const content =
              searchKey && text !== undefined ? (
                <TranscriptSearchHighlight
                  text={text}
                  highlights={highlightMap.get(searchKey)}
                  currentMatchIndex={currentMatchIndex}
                />
              ) : (
                text
              );

            return (
              <span
                className={`word inline-block ${typeof start === 'number' ? '' : 'cursor-default'} ${className ?? ''}`.trim()}
                onClick={() => {
                  if (typeof start === 'number') {
                    onWordClick(start);
                  }
                }}
                title={typeof start === 'number' ? formatTime(start / 1000) : undefined}
                style={{
                  backgroundColor,
                  boxShadow: isActive ? 'inset 0 0 0 2px #0284c7' : undefined,
                }}
              >
                {isStrong ? <strong>{content}</strong> : content}
              </span>
            );
          };

          if (token.operation === 'substitution') {
            return (
              <Fragment key={tokenIdx}>
                {renderWord({
                  text: token.primaryText,
                  start: token.primaryStart,
                  field: 'primaryStart',
                  className: 'line-through',
                })}
                {' / '}
                {renderWord({
                  text: token.compareText,
                  start: token.compareStart,
                  field: 'compareStart',
                  isStrong: true,
                  searchKey: `${utteranceIdx}:${tokenIdx}:compare`,
                })}
                &nbsp;
              </Fragment>
            );
          }

          if (token.operation === 'addition') {
            return (
              <Fragment key={tokenIdx}>
                {renderWord({
                  text: token.compareText,
                  start: token.compareStart,
                  field: 'compareStart',
                  isStrong: true,
                  searchKey: `${utteranceIdx}:${tokenIdx}:compare`,
                })}
                &nbsp;
              </Fragment>
            );
          }

          if (token.operation === 'deletion') {
            return (
              <Fragment key={tokenIdx}>
                {renderWord({
                  text: token.primaryText,
                  start: token.primaryStart,
                  field: 'primaryStart',
                  className: 'line-through',
                })}
                &nbsp;
              </Fragment>
            );
          }

          return (
            <Fragment key={tokenIdx}>
              {renderWord({
                text: token.primaryText,
                start: token.primaryStart,
                field: 'primaryStart',
                searchKey: `${utteranceIdx}:${tokenIdx}:primary`,
              })}
              &nbsp;
            </Fragment>
          );
        })}
      </span>
    </div>
  ),
);

CompareUtteranceRow.displayName = 'CompareUtteranceRow';

export const CompareTranscriptLayout = ({
  utterances,
  playbackTime,
  onWordClick,
  maxColorTimeDeltaMs,
  searchFocusSignal,
}: TProps) => {
  const currentTimeMs = playbackTime * 1000;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const virtuosoRef = useRef<VirtuosoHandle | null>(null);
  const searchableWords = useMemo(() => flattenCompareTokens(utterances), [utterances]);
  const {
    query,
    setQuery,
    caseSensitive,
    setCaseSensitive,
    wholeWord,
    setWholeWord,
    matchCount,
    currentMatchIndex,
    currentMatchNumber,
    highlightMap,
    currentMatch,
    scrollTarget,
    goToNextMatch,
    goToPreviousMatch,
  } = useTranscriptSearch({ words: searchableWords });

  useEffect(() => {
    if (!scrollTarget || !virtuosoRef.current) {
      return;
    }

    virtuosoRef.current.scrollToIndex({
      index: scrollTarget.match.itemIndex,
      align: 'center',
      behavior: 'auto',
    });

    if (containerRef.current) {
      return scrollSearchMatchIntoView(containerRef.current);
    }
  }, [scrollTarget]);

  const speakers = useMemo(() => Array.from(new Set(utterances.map((utterance) => utterance.speaker))), [utterances]);
  const speakerNameLength = useMemo(() => {
    return speakers.reduce((longest, speaker) => Math.max(longest, formatSpeakerLabel(speaker).length), 0);
  }, [speakers]);
  const currentMatchItemIndexes = useMemo(
    () => new Set<number>(currentMatch?.spans.map((span) => span.itemIndex) ?? []),
    [currentMatch],
  );
  const onWordClickRef = useRef(onWordClick);
  onWordClickRef.current = onWordClick;
  const handleWordClick = useCallback((timestamp: number) => onWordClickRef.current(timestamp), []);
  const itemContent = useCallback(
    (utteranceIdx: number, utterance: TranscriptCompareUtterance) => (
      <CompareUtteranceRow
        utterance={utterance}
        utteranceIdx={utteranceIdx}
        speakerNameLength={speakerNameLength}
        currentTimeMs={currentTimeMs}
        maxColorTimeDeltaMs={maxColorTimeDeltaMs}
        highlightMap={highlightMap}
        currentMatchIndex={currentMatchItemIndexes.has(utteranceIdx) ? currentMatchIndex : -1}
        onWordClick={handleWordClick}
      />
    ),
    [
      currentMatchIndex,
      currentMatchItemIndexes,
      currentTimeMs,
      handleWordClick,
      highlightMap,
      maxColorTimeDeltaMs,
      speakerNameLength,
    ],
  );

  return (
    <div ref={containerRef} className="mt-[var(--transcript-toolbar-clearance,6rem)]">
      <TranscriptSearchBox
        namePrefix="compare"
        query={query}
        matchCount={matchCount}
        currentMatchNumber={currentMatchNumber}
        caseSensitive={caseSensitive}
        wholeWord={wholeWord}
        focusSignal={searchFocusSignal}
        className="sticky top-[var(--transcript-toolbar-bottom,0px)] z-[9] mb-4"
        onQueryChange={setQuery}
        onCaseSensitiveChange={setCaseSensitive}
        onWholeWordChange={setWholeWord}
        onPrevious={goToPreviousMatch}
        onNext={goToNextMatch}
      />

      <div className="mb-4">
        {speakers.map((speaker, idx) => (
          <div key={speaker}>
            <p className="text-sm">
              {speaker ? `Speaker ${getSpeakerIndexLabel(idx)}: ${speaker}` : 'Unknown Speaker'}
            </p>
          </div>
        ))}
      </div>

      <div className="mb-2 flex flex-wrap items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <span
            className="inline-block h-3 w-20 rounded border border-slate-200"
            style={{
              background: `linear-gradient(to right, ${getRgba(NEGATIVE_COLOR, 0)}, ${getRgba(NEGATIVE_COLOR, MAX_COLOR_ALPHA)})`,
            }}
          />
          <span>Compared earlier (-)</span>
        </div>

        <div className="flex items-center gap-2">
          <span className="inline-block h-3 w-5 rounded border border-slate-200 bg-[var(--color-surface)]" />
          <span>No offset</span>
        </div>

        <div className="flex items-center gap-2">
          <span
            className="inline-block h-3 w-20 rounded border border-slate-200"
            style={{
              background: `linear-gradient(to right, ${getRgba(POSITIVE_COLOR, 0)}, ${getRgba(POSITIVE_COLOR, MAX_COLOR_ALPHA)})`,
            }}
          />
          <span>Compared later (+)</span>
        </div>
      </div>

      <Virtuoso ref={virtuosoRef} useWindowScroll data={utterances} itemContent={itemContent} />
    </div>
  );
};
